import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { DeepLinkUtil } from '@edtech-ui/core/utils/deep-link.util';

@Component({
	selector: 'app-featured-mock-interview',
	standalone: true,
	imports: [CommonModule, RouterModule, FormsModule, HttpClientModule],
	templateUrl: './featured-mock-interview.component.html',
	styleUrl: './featured-mock-interview.component.scss'
})
export class FeaturedMockInterviewComponent implements OnInit, OnDestroy {

	isBrowser: boolean;
	isDashboard = false;

	mockInterviews: any[] = [];
	searchTerm = '';
	isLoading = false;
	isLoadingMore = false;
	hasMore = false;
	page = 1;
	pageSize = 9;
	totalRecords = 0;

	isAtStart = true;
	isAtEnd = false;

	private destroy$ = new Subject<void>();
	private searchTrigger$ = new Subject<void>();
	private scrollTimeout: any = null;
	private readonly SCROLL_DEBOUNCE_MS = 150;

	@ViewChild('interviewGrid') interviewGrid!: ElementRef;

	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		private router: Router,
		private http: HttpClient,
		private toast: ToastrService
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (!this.isBrowser) return;
		this.debouncedScrollCheck();
	}

	ngOnInit(): void {
		if (this.isBrowser) {
			this.isDashboard = !!localStorage.getItem('token');
		}
		this.setupSearchTrigger();
		this.fetchMockInterviews(true);
	}

	setupSearchTrigger(): void {
		this.searchTrigger$
			.pipe(debounceTime(350), takeUntil(this.destroy$))
			.subscribe(() => {
				this.fetchMockInterviews(true);
			});
	}

	fetchMockInterviews(isInitial = true): void {
		if (isInitial) {
			this.isLoading = true;
			this.page = 1;
			this.mockInterviews = [];
			this.isAtStart = true;
			this.isAtEnd = false;
		} else {
			this.isLoadingMore = true;
		}

		let url = `${environment.hrmsApiUrl}/rounds?is_public=true&publish_status=Published&page=${this.page}&limit=${this.pageSize}`;

		if (this.searchTerm?.trim()) {
			url += '&search=' + encodeURIComponent(this.searchTerm.trim());
		}

		this.http.get<any>(url)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					const dataObj = res?.Data || res?.data;
					const newRounds = dataObj && Array.isArray(dataObj.rounds) ? dataObj.rounds : [];

					if (isInitial) {
						this.mockInterviews = newRounds;
					} else {
						const existingIds = new Set(this.mockInterviews.map(i => i.id || i._id || i.screening_round_id));
						const uniqueNew = newRounds.filter((i: any) => !existingIds.has(i.id || i._id || i.screening_round_id));
						this.mockInterviews = [...this.mockInterviews, ...uniqueNew];
					}

					this.totalRecords = dataObj?.pagination?.total ?? dataObj?.total ?? this.mockInterviews.length;

					if (dataObj?.pagination) {
						this.hasMore = this.mockInterviews.length < this.totalRecords;
					} else {
						this.hasMore = newRounds.length >= this.pageSize;
					}

					this.isLoading = false;
					this.isLoadingMore = false;
					setTimeout(() => this.checkScrollPosition(), 100);
				},
				error: (err) => {
					console.error('Failed to fetch mock interviews', err);
					this.isLoading = false;
					this.isLoadingMore = false;
					if (isInitial) {
						this.mockInterviews = [];
						this.totalRecords = 0;
					}
					this.toast.error('Failed to load practice interviews');
				}
			});
	}

	loadNextPage(): void {
		if (!this.hasMore || this.isLoadingMore || this.isLoading) return;
		this.page++;
		this.fetchMockInterviews(false);
	}

	onSearchChange(): void {
		this.searchTrigger$.next();
	}

	clearSearch(): void {
		this.searchTerm = '';
		this.fetchMockInterviews(true);
	}

	trackByRoundId(index: number, item: any): string {
		return item?.id || item?._id || item?.screening_round_id || index.toString();
	}

	getCardScrollAmount(): number {
		if (!this.interviewGrid?.nativeElement || !this.isBrowser) return 900;
		const grid = this.interviewGrid.nativeElement;
		const cards = grid.querySelectorAll('.mock-interview-card');

		if (cards.length >= 2) {
			const singleSpan = (cards[1] as HTMLElement).offsetLeft - (cards[0] as HTMLElement).offsetLeft;
			if (window.innerWidth >= 1024) {
				if (cards.length >= 4) {
					const threeSpan = (cards[3] as HTMLElement).offsetLeft - (cards[0] as HTMLElement).offsetLeft;
					if (threeSpan > 0) return threeSpan;
				}
				return singleSpan * 3;
			} else if (window.innerWidth >= 640) {
				return singleSpan * 2;
			} else {
				return singleSpan;
			}
		}
		return grid.clientWidth || 300;
	}

	scrollLeft(): void {
		if (!this.interviewGrid?.nativeElement || !this.isBrowser) return;
		const scrollAmount = this.getCardScrollAmount();
		this.interviewGrid.nativeElement.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
		setTimeout(() => this.checkScrollPosition(), 400);
	}

	scrollRight(): void {
		if (!this.interviewGrid?.nativeElement || !this.isBrowser) return;
		const scrollAmount = this.getCardScrollAmount();
		this.interviewGrid.nativeElement.scrollBy({ left: scrollAmount, behavior: 'smooth' });
		setTimeout(() => this.checkScrollPosition(), 400);
	}

	onGridScroll(): void {
		this.debouncedScrollCheck();
	}

	private debouncedScrollCheck(): void {
		if (this.scrollTimeout) {
			clearTimeout(this.scrollTimeout);
		}
		this.scrollTimeout = setTimeout(() => {
			this.checkScrollPosition();
			this.scrollTimeout = null;
		}, this.SCROLL_DEBOUNCE_MS);
	}

	checkScrollPosition(): void {
		if (!this.interviewGrid?.nativeElement || !this.isBrowser) return;
		const el = this.interviewGrid.nativeElement;
		this.isAtStart = el.scrollLeft <= 5;
		const maxScroll = el.scrollWidth - el.clientWidth;
		this.isAtEnd = maxScroll <= 0 || el.scrollLeft >= maxScroll - 5;

		if (el.scrollLeft >= maxScroll - 100 && this.hasMore && !this.isLoadingMore && !this.isLoading) {
			this.loadNextPage();
		}
	}

	startMockInterview(roundId: string): void {
		if (DeepLinkUtil.isAndroid()) {
			window.location.href = DeepLinkUtil.getAndroidIntentUrl('student-dashboard/ai-interview-dashboard');
			return;
		}

		if (!roundId) {
			this.toast.error('Invalid interview selected');
			return;
		}

		const token = this.isBrowser ? localStorage.getItem('token') : null;
		if (!token) {
			if (this.isBrowser) {
				localStorage.setItem('pending_practice_round_id', roundId);
			}
			this.router.navigate(['/login'], { queryParams: { redirectUrl: '/student-dashboard/ai-interview-dashboard' } });
			return;
		}

		const interview = this.mockInterviews.find(i => (i.id === roundId || i._id === roundId || i.screening_round_id === roundId));
		if (!interview) {
			this.toast.error('Interview not found');
			return;
		}

		let uniqueToken = interview.unique_token;
		if (!uniqueToken && interview.interview_url) {
			const parts = interview.interview_url.split('/');
			if (parts.length >= 2) {
				uniqueToken = parts[parts.length - 2];
			}
		}

		if (uniqueToken) {
			const urlTree = this.router.createUrlTree(['/interview.ai', uniqueToken, roundId], { queryParams: { is_b2c_practice: 'true' } });
			const url = this.router.serializeUrl(urlTree);
			window.open(url, '_blank');
		} else {
			this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.scrollTimeout) {
			clearTimeout(this.scrollTimeout);
			this.scrollTimeout = null;
		}
	}
}
